import {useEffect, useRef} from "react";

import {useQueryClient} from "@tanstack/react-query";
import {toast as sonnerToast} from "sonner";

import useToast from "@/src/modules/core/hooks/use-toast";
import {useSocket} from "@/src/modules/core/context/socket";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {useStoredMe} from "@/src/modules/core/store/me";

import {invalidateAllDirectMessageThreadLists} from "@/src/modules/direct-messages/lib/direct-message-query-cache";
import {dispatchShiftComplianceGateFromNotification} from "../lib/dispatch-shift-compliance-gate-from-notification";
import {triggerNotificationAttention, getNotificationAttentionDedupeKey} from "../lib/notification-attention";
import {
  coerceNotificationFeedRealtimeTopic,
  coerceNotificationFeedRealtimeType,
  filterNotificationItemsForViewer,
  getNotificationBellTopics,
  isResidentBellTopic,
  isShiftNotificationTopic,
  isUrgentNotificationType,
} from "../lib/notification-feed-topics";
import {getNotificationCardDisplay, getNotificationOneLineTitle} from "../lib/notification-display";
import {notificationFeedService} from "../services";
import {useNotificationFeedStore} from "../store";
import type {
  NotificationDiscardedRealtimeEvent,
  NotificationItem,
  NotificationTopic,
  NotificationType,
} from "../types";
import {NOTIFICATION_FEED_QUERY_KEY} from "./use-notification-feed";

/**
 * Laravel notification broadcasts (Echo `.notification(cb)`) arrive as one of these Pusher `event` names.
 * Feed rows use `stored_vehicle.*` or `shift.*` types; other types are ignored here.
 * @see md_files/20260508_2_NOTIFICATIONS_FEED_AND_REMINDERS/05_FE_EVENTS_STORED_VEHICLE_NOTIFICATIONS.md
 */
const NOTIFICATION_ARRIVAL_EVENT_NAMES = [
  "Illuminate\\Notifications\\Events\\BroadcastNotificationCreated",
  ".Illuminate\\Notifications\\Events\\BroadcastNotificationCreated",
  "notifications.created",
  ".notifications.created",
  "notification.created",
] as const;

/** Custom broadcast `broadcastAs`: `notifications.discarded` — Echo listens as `.notifications.discarded`. */
const NOTIFICATION_DISCARDED_EVENT_NAMES = [
  "notifications.discarded",
  ".notifications.discarded",
] as const;

/** In-app toast lifetimes (ms). Web Push may stay sticky; tab toasts should auto-dismiss. */
const NOTIFICATION_TOAST_DURATION_MS = {
  "stored_vehicle.tracking_active": 5_000,
  "stored_vehicle.cite_eligible": 10_000,
  "shift.break_alert": 10_000,
  "shift.end_alert": 10_000,
} as const satisfies Partial<Record<NotificationType, number>>;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function asNullableString(value: unknown): string | null {
  if (value === null) {
    return null;
  }
  return asString(value);
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getToastId(notificationId: string): string {
  return `notification-feed:${notificationId}`;
}

function isFocusedTab(): boolean {
  return document.visibilityState === "visible" && document.hasFocus();
}

function unwrapNotificationRoot(payload: unknown): Record<string, unknown> | null {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }

  const nestedNotification = asRecord(root.notification);
  if (nestedNotification) {
    return nestedNotification;
  }

  const data = asRecord(root.data);
  const dataNotification = data ? asRecord(data.notification) : null;
  if (dataNotification) {
    return dataNotification;
  }

  return root;
}

function normalizeNotificationItem(payload: unknown): NotificationItem | null {
  const root = unwrapNotificationRoot(payload);
  if (!root) {
    return null;
  }

  const data = asRecord(root.data) ?? {};
  const id = asString(root.id) ?? asString(data.id);
  const topicRaw = asString(root.topic) ?? asString(data.topic);
  const rawType = asString(root.type) ?? asString(data.type);
  const type = coerceNotificationFeedRealtimeType(rawType);
  const topic = coerceNotificationFeedRealtimeTopic(topicRaw);

  if (!id || !topic || !type) {
    return null;
  }

  const rawSubject = asRecord(root.subject) ?? asRecord(data.subject);
  const payloadRecord = asRecord(root.payload) ?? asRecord(data.payload) ?? data;
  const subjectId =
    asNumber(rawSubject?.id ?? root.subject_id ?? data.subject_id ?? root.subjectId ?? data.subjectId) ??
    asNumber(payloadRecord.direct_message_thread_id) ??
    asNumber(payloadRecord.briefing_board_id) ??
    asNumber(payloadRecord.house_hold_id) ??
    asNumber(payloadRecord.household_id) ??
    (type === "shift.break_alert" ? asNumber(payloadRecord.shift_break_id) : null) ??
    (type === "shift.end_alert" ? asNumber(payloadRecord.shift_id) : null) ??
    asNumber(payloadRecord.shift_break_id) ??
    asNumber(payloadRecord.shift_id);
  const subjectType =
    asString(rawSubject?.type ?? root.subject_type ?? data.subject_type ?? root.subjectType ?? data.subjectType) ??
    topic;
  if (!subjectId) {
    return null;
  }

  const title =
    asString(root.title) ??
    asString(data.title) ??
    "";
  const message =
    asString(root.message) ??
    asString(root.body) ??
    asString(data.message) ??
    asString(data.body) ??
    "";

  return {
    id,
    topic,
    type,
    subject: {
      type: subjectType,
      id: subjectId,
    },
    title,
    message,
    payload: payloadRecord,
    read_at: asNullableString(root.read_at),
    discarded_at: asNullableString(root.discarded_at),
    created_at: asString(root.created_at) ?? new Date().toISOString(),
  };
}

function normalizeDiscardedPayload(payload: unknown): NotificationDiscardedRealtimeEvent | null {
  const root = asRecord(payload);
  if (!root) {
    return null;
  }

  const nested = asRecord(root.data) ?? {};
  const topicRaw = asString(root.topic) ?? asString(nested.topic);
  const coercedTopic = coerceNotificationFeedRealtimeTopic(topicRaw);
  if (topicRaw && !coercedTopic) {
    return null;
  }

  const rawIds = root.notification_ids ?? root.ids ?? nested.notification_ids ?? nested.ids;
  if (!Array.isArray(rawIds)) {
    return null;
  }

  const notificationIds = rawIds
    .map((id) => asString(id))
    .filter((id): id is string => Boolean(id));

  if (!notificationIds.length) {
    return null;
  }

  const subjectId = asNumber(root.subject_id ?? nested.subject_id) ?? 0;
  const topic = (coercedTopic ?? "stored_vehicle") as NotificationTopic;

  return {
    topic,
    subject_type: asString(root.subject_type ?? nested.subject_type) ?? topic,
    subject_id: subjectId,
    notification_ids: notificationIds,
    reason: asString(root.reason ?? nested.reason) ?? "deleted",
    discarded_at: asString(root.discarded_at ?? nested.discarded_at) ?? new Date().toISOString(),
  };
}

export function useNotificationFeedRealtime() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const {socket} = useSocket();
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const {prependFromRealtime, removeByIdsFromDiscardEvent, hydrateFromApi} = useNotificationFeedStore();
  const hasSeenInitialOpen = useRef(false);

  useEffect(() => {
    const showRealtimeToast = (item: NotificationItem) => {
      if (!isFocusedTab()) {
        return;
      }

      const toastId = getToastId(item.id);
      const {body} = getNotificationCardDisplay(item);
      const toastTitle = getNotificationOneLineTitle(item);
      const toastBody = body ?? undefined;

      const duration =
        item.type in NOTIFICATION_TOAST_DURATION_MS
          ? NOTIFICATION_TOAST_DURATION_MS[item.type as keyof typeof NOTIFICATION_TOAST_DURATION_MS]
          : 5_000;

      if (isUrgentNotificationType(item.type)) {
        toast.warning(toastTitle, {
          id: toastId,
          description: toastBody ?? undefined,
          duration,
        });
        return;
      }

      toast.info(toastTitle, {
        id: toastId,
        description: toastBody ?? undefined,
        duration,
      });
    };

    const onArrival = (event: Event) => {
      const payload = (event as CustomEvent<unknown>).detail;
      const item = normalizeNotificationItem(payload);
      if (!item) {
        return;
      }

      if (viewingAsUser && isShiftNotificationTopic(item.topic)) {
        return;
      }

      if (me?.isResident && !isResidentBellTopic(item.topic)) {
        return;
      }

      prependFromRealtime(item);
      if (item.topic === "direct_message") {
        invalidateAllDirectMessageThreadLists(queryClient);
      }
      dispatchShiftComplianceGateFromNotification(item);
      if (isUrgentNotificationType(item.type)) {
        triggerNotificationAttention(item.type, getNotificationAttentionDedupeKey(item));
      }
      showRealtimeToast(item);
    };

    const onDiscarded = (event: Event) => {
      const payload = (event as CustomEvent<unknown>).detail;
      const discarded = normalizeDiscardedPayload(payload);
      if (!discarded) {
        return;
      }

      if (viewingAsUser && isShiftNotificationTopic(discarded.topic)) {
        return;
      }

      removeByIdsFromDiscardEvent(discarded.notification_ids);
      discarded.notification_ids.forEach((notificationId) => {
        sonnerToast.dismiss(getToastId(notificationId));
      });

      if (isShiftNotificationTopic(discarded.topic)) {
        void reconcileCompliance(queryClient);
      }
    };

    NOTIFICATION_ARRIVAL_EVENT_NAMES.forEach((eventName) => {
      window.addEventListener(eventName, onArrival);
    });
    NOTIFICATION_DISCARDED_EVENT_NAMES.forEach((eventName) => {
      window.addEventListener(eventName, onDiscarded);
    });

    return () => {
      NOTIFICATION_ARRIVAL_EVENT_NAMES.forEach((eventName) => {
        window.removeEventListener(eventName, onArrival);
      });
      NOTIFICATION_DISCARDED_EVENT_NAMES.forEach((eventName) => {
        window.removeEventListener(eventName, onDiscarded);
      });
    };
  }, [me?.isResident, prependFromRealtime, queryClient, removeByIdsFromDiscardEvent, toast, viewingAsUser]);

  useEffect(() => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }

    if (!hasSeenInitialOpen.current) {
      hasSeenInitialOpen.current = true;
      return;
    }

    const isResident = Boolean(me?.isResident);
    const bellTopics = getNotificationBellTopics(isResident);

    void (async () => {
      try {
        const unreadFeed = await notificationFeedService.getUnread({
          topic: bellTopics,
          per_page: 25,
        });
        const rows = filterNotificationItemsForViewer(unreadFeed.data, viewingAsUser);
        hydrateFromApi({...unreadFeed, data: rows});
        for (const item of rows) {
          if (!item.read_at && !item.discarded_at) {
            dispatchShiftComplianceGateFromNotification(item);
          }
        }
        await queryClient.invalidateQueries({
          queryKey: [NOTIFICATION_FEED_QUERY_KEY],
        });
      } catch {
        // Intentionally swallowed: reconnect recovery is best-effort.
      }
    })();
  }, [socket, hydrateFromApi, me?.isResident, queryClient, viewingAsUser]);
}
